// src/services/UserService.ts
import { api } from "../api";

interface UserState {
  id: string;
  name: string;
  membership?: 'PRO' | 'FREE';
  rateLimit?: {
    nextAvailableTime: string;
    cooldownRemaining?: number;
  } | null;
  status: 'unknown' | 'checking' | 'loaded' | 'error';
  lastChecked: number;
}

interface UserStatusListenerParams {
  canPerformAction: boolean;
  statusMessage: string | null;
  membership?: 'PRO' | 'FREE';
  rateLimit?: {
    nextAvailableTime: string;
    cooldownRemaining?: number;
  } | null;
  userStatus?: any;
}

type UserStatusListener = (status: UserStatusListenerParams) => void;

class UserService {
  private userState: UserState = {
    id: '',
    name: '',
    membership: undefined,
    rateLimit: undefined,
    status: 'unknown',
    lastChecked: 0
  };

  private listeners: UserStatusListener[] = [];
  private quotaCheckPromise: Promise<any> | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  setUserInfo(id: string, name: string) {
    this.userState.id = id;
    this.userState.name = name;
    // Reset cache when user changes
    this.userState.membership = undefined;
    this.userState.rateLimit = undefined;
    this.userState.status = 'unknown';
    this.userState.lastChecked = 0;
  }

  getUserInfo() {
    return {
      id: this.userState.id,
      name: this.userState.name
    };
  }

  addStatusListener(listener: UserStatusListener) {
    this.listeners.push(listener);

    // Immediately call with current state if available
    if (this.userState.status === 'loaded' && this.userState.membership) {
      const isPro = this.userState.membership === 'PRO';
      const isRateLimited = this.userState.rateLimit && new Date(this.userState.rateLimit.nextAvailableTime) > new Date();

      listener({
        canPerformAction: isPro || !isRateLimited,
        statusMessage: isRateLimited && !isPro
          ? `Rate limit active. Please wait. Upgrade to PRO for unlimited access.`
          : null,
        membership: this.userState.membership,
        rateLimit: this.userState.rateLimit
      });
    }

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(status: UserStatusListenerParams) {
    this.listeners.forEach(listener => listener(status));
  }

  private isCacheValid(): boolean {
    const now = Date.now();
    return this.userState.status === 'loaded' && 
           (now - this.userState.lastChecked) < this.CACHE_DURATION;
  }

  async checkUserStatus(forceRefresh = false): Promise<UserStatusListenerParams> {
    if (!this.userState.id) {
      const result = {
        canPerformAction: false,
        statusMessage: "User ID is required. Please restart the plugin."
      };
      this.notifyListeners(result);
      return result;
    }

    // Return cached result if valid and not forcing refresh
    if (!forceRefresh && this.isCacheValid() && this.userState.membership) {
      const isPro = this.userState.membership === 'PRO';
      const isRateLimited = this.userState.rateLimit && new Date(this.userState.rateLimit.nextAvailableTime) > new Date();

      const result = {
        canPerformAction: isPro || !isRateLimited,
        statusMessage: isRateLimited && !isPro
          ? `Rate limit active. Please wait. Upgrade to PRO for unlimited access.`
          : null,
        membership: this.userState.membership,
        rateLimit: this.userState.rateLimit
      };
      return result;
    }

    // If there's already a check in progress, return that promise
    if (this.quotaCheckPromise) {
      return this.quotaCheckPromise;
    }

    this.userState.status = 'checking';

    this.quotaCheckPromise = (async () => {
      try {
        const userStatus = await api.getUserStatus(this.userState.id, forceRefresh);

        if (!userStatus.isSuccess) {
          this.userState.status = 'error';
          const result = {
            canPerformAction: false,
            statusMessage: "Unable to verify user status. Please try again later."
          };
          this.notifyListeners(result);
          return result;
        }

        // Update cached state
        this.userState.membership = userStatus.membership;
        this.userState.rateLimit = userStatus.rateLimit;
        this.userState.status = 'loaded';
        this.userState.lastChecked = Date.now();

        const isPro = userStatus.membership === 'PRO';
        const isRateLimited = userStatus.rateLimit && new Date(userStatus.rateLimit.nextAvailableTime) > new Date();

        let statusMessage = null;
        if (isRateLimited && !isPro && userStatus.rateLimit) {
          const nextAvailable = new Date(userStatus.rateLimit.nextAvailableTime);
          const now = new Date();
          const timeRemaining = Math.ceil((nextAvailable.getTime() - now.getTime()) / (1000 * 60));
          const hours = Math.floor(timeRemaining / 60);
          const minutes = timeRemaining % 60;

          let timeStr = '';
          if (hours > 0) timeStr += `${hours}h `;
          if (minutes > 0 || hours === 0) timeStr += `${minutes}m`;

          statusMessage = `Rate limit active. Please wait ${timeStr}. Upgrade to PRO for unlimited access.`;
        }

        const result = {
          canPerformAction: isPro || !isRateLimited,
          statusMessage,
          membership: this.userState.membership,
          rateLimit: this.userState.rateLimit,
          userStatus: userStatus
        };

        this.notifyListeners(result);
        return result;
      } catch {
        this.userState.status = 'error';
        // Error sent to API via logger - no console output
        
        // Allow the action but return a warning
        const result = {
          canPerformAction: true,
          statusMessage: "Could not verify remaining imports. Your action may be limited if you've reached your quota."
        };
        this.notifyListeners(result);
        return result;
      } finally {
        this.quotaCheckPromise = null;
      }
    })();

    return this.quotaCheckPromise;
  }

  updateFromResponse(response: { membership?: 'PRO' | 'FREE'; rateLimit?: { nextAvailableTime: string } }) {
    if (response.membership) {
      this.userState.membership = response.membership;
      this.userState.rateLimit = response.rateLimit;
      this.userState.lastChecked = Date.now();
      this.userState.status = 'loaded';

      const isPro = response.membership === 'PRO';
      const isRateLimited = response.rateLimit && new Date(response.rateLimit.nextAvailableTime) > new Date();

      // Notify listeners of the update
      this.notifyListeners({
        canPerformAction: isPro || !isRateLimited,
        statusMessage: isRateLimited && !isPro
          ? `Rate limit active. Please wait. Upgrade to PRO for unlimited access.`
          : null,
        membership: this.userState.membership,
        rateLimit: this.userState.rateLimit
      });
    }
  }

  getMembershipInfo() {
    return {
      membership: this.userState.membership,
      rateLimit: this.userState.rateLimit
    };
  }

  clearCache() {
    this.userState.membership = undefined;
    this.userState.rateLimit = undefined;
    this.userState.status = 'unknown';
    this.userState.lastChecked = 0;
  }

}

// Export singleton instance
export const userService = new UserService();